'use client';

import { useQueryClient } from '@tanstack/react-query';
import type { FilterOptions } from '@veranda/shared/components/pages/product-listing';
import {
  type ProductListingPageBaseProps,
  buildInitialFilterValues,
} from '@veranda/shared/components/pages/product-listing/client';
import {
  LinkPaginationNavigationButton,
  LinkPaginationPageNumberButton,
} from '@veranda/shared/components/ui/link-pagination';
import {
  PaginationEllipsisItem,
  usePaginationRange,
} from '@veranda/shared/components/ui/pagination';
import { QUERY_KEYS, cn } from '@veranda/shared/lib';
import type { CSSProperties } from 'react';

export function StaticPaginationFallback({
  slug,
  data: _data,
  filterOptions,
  className,
  style,
}: {
  slug: string;
  data: ProductListingPageBaseProps;
  filterOptions: FilterOptions[];
  className?: string;
  style?: CSSProperties;
}) {
  const queryClient = useQueryClient();

  // Get the prefetched page count from the query cache
  const initialFilterValues = buildInitialFilterValues(filterOptions);
  const pageCount =
    queryClient.getQueryData<number>([
      QUERY_KEYS.PRODUCT_LISTING_PAGE_COUNT,
      slug,
      initialFilterValues,
    ]) ?? 1;

  // Static pagination always shows page 1 as current for SEO
  const currentPage = 1;
  const { pageNumbers, hasNextPage, hasPreviousPage } = usePaginationRange({
    currentPage,
    totalPages: pageCount,
  });

  // Helper function to create pagination URLs
  function createPageUrl(page: number): string {
    if (page === 1) {
      return `/collections/${slug}`;
    }
    return `/collections/${slug}?page=${page}`;
  }

  if (pageCount <= 1) {
    return null;
  }

  return (
    <div className={cn('flex justify-center', className)} style={style}>
      <nav
        className="relative z-0 inline-grid auto-cols-min grid-flow-col-dense gap-2 shadow-sm md:gap-3"
        aria-label="Pagination"
      >
        <LinkPaginationNavigationButton
          direction="previous"
          enabled={hasPreviousPage}
          href={hasPreviousPage ? createPageUrl(currentPage - 1) : undefined}
        />
        {pageNumbers.map((pageNumber: string | number, index: number) =>
          pageNumber === '...' ? (
            <PaginationEllipsisItem key={`ellipsis-${index}`} />
          ) : (
            <LinkPaginationPageNumberButton
              key={pageNumber}
              pageNumber={pageNumber as number}
              currentPage={currentPage}
              href={createPageUrl(pageNumber as number)}
            />
          )
        )}
        <LinkPaginationNavigationButton
          direction="next"
          enabled={hasNextPage}
          href={hasNextPage ? createPageUrl(currentPage + 1) : undefined}
        />
      </nav>
    </div>
  );
}
